import React, { useEffect, useState } from 'react';
import T from 'Components/Helpers/translate';
import { useSoftDeployAccess } from 'App/components/hooks';
import { useMetadata, useSelf } from 'App/components/hooks/appHooks';
import { cn } from 'App/shadcn/lib/utils';
import {
	fetchAirscale,
	fetchWaterfall,
	fetchWaterfallKeys,
	type Channel,
	type ContactWaterfall,
	type KeySource,
	type Provider
} from '../enrichment/enrichmentApi';
import { LINK, PILL } from './LookupParts';
import {
	companySourceSteps,
	waterfallSteps,
	type CompanySourceAvailability,
	type LookupRun,
	type StepStatus
} from './waterfallSteps';
import { PROVIDER_NAMES as LOOKUP_PROVIDER_NAMES, type LookupCompanySource } from './lookupApi';

/** Settings › Prospecting › Enrichment, where the order is changed since the tab went (WEB-13187). */
const ENRICHMENT_PATH = '/admin/prospecting-enrichment';

const CHANNEL_TABS: Array<{ id: Channel; langKey: string }> = [
	{ id: 'email', langKey: 'prospectingTools.lookup.channel.email' },
	{ id: 'phoneNumber', langKey: 'voice.phoneNumber' }
];

const KEY_LABELS: Record<KeySource, string> = {
	client: 'prospectingTools.enrichment.key.own',
	default: 'prospectingTools.enrichment.key.upsales',
	none: 'prospectingTools.enrichment.key.none'
};

const STATUS: Record<StepStatus, { langKey: string; className: string }> = {
	found: {
		langKey: 'prospectingTools.lookup.waterfall.status.found',
		className: 'bg-success-light text-success-dark'
	},
	noMatch: { langKey: 'prospectingTools.lookup.waterfall.status.noMatch', className: 'bg-grey-2 text-grey-11' },
	notNeeded: { langKey: 'prospectingTools.lookup.waterfall.status.notNeeded', className: 'bg-grey-1 text-grey-10' },
	skippedForCredits: {
		langKey: 'prospectingTools.lookup.waterfall.status.skippedForCredits',
		className: 'bg-alert-light text-alert-dark'
	}
};

const COMPANY_SOURCE_LABELS: Record<Exclude<LookupCompanySource, 'apollo' | 'airscale'>, string> = {
	register: 'prospectingTools.lookup.companySource.register',
	crm: 'prospectingTools.lookup.companySource.crm'
};

/** The sources a company lookup asks, in order, and what each did (WEB-13098). */
const CompanySourceList = ({ steps }: { steps: ReturnType<typeof companySourceSteps> }) => (
	<ol className="m-0 list-none">
		{steps.map((step, index) => {
			const label =
				step.source === 'apollo' || step.source === 'airscale'
					? LOOKUP_PROVIDER_NAMES[step.source]
					: T(COMPANY_SOURCE_LABELS[step.source]);
			return (
				<li
					key={step.source}
					aria-label={label}
					className="flex items-center gap-3 border-b border-grey-2 py-3 last:border-b-0"
				>
					<span
						aria-hidden
						className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-grey-2 text-body-sm font-semibold text-grey-11"
					>
						{index + 1}
					</span>
					<div className="min-w-0 flex-1 text-body-md font-medium text-grey-13">{label}</div>
					<span className={cn(PILL, STATUS[step.status].className)}>{T(STATUS[step.status].langKey)}</span>
				</li>
			);
		})}
	</ol>
);

/** Which sources can run here: the same keys and Airscale setting the server reads (WEB-13771). */
const useSourceAvailability = (): CompanySourceAvailability => {
	const hasAirscaleFlag = useSoftDeployAccess('AIRSCALE_INTEGRATION');
	const euOnly = useMetadata()?.params?.EuOnly === true;
	const [airscaleOn, setAirscaleOn] = useState(false);
	// Assumed connected until the keys answer, so a slow read never shows a false 'Not connected'.
	const [apollo, setApollo] = useState(true);
	useEffect(() => {
		let cancelled = false;
		fetchWaterfallKeys()
			.then(keys => !cancelled && setApollo(keys.apollo !== 'none'))
			.catch((err: unknown) => console.error('[Lookup] reading the waterfall keys failed', err));
		if (hasAirscaleFlag && !euOnly) {
			fetchAirscale()
				.then(({ enabled }) => !cancelled && setAirscaleOn(enabled))
				.catch((err: unknown) => console.error('[Lookup] reading the Airscale setting failed', err));
		}
		return () => {
			cancelled = true;
		};
	}, [hasAirscaleFlag, euOnly]);
	return { airscale: hasAirscaleFlag && !euOnly && airscaleOn, apollo };
};

/** Which source found the shown company, and the ones asked before it (WEB-13098). */
const CompanySources = ({ run }: { run: LookupRun | null }) => {
	const steps = companySourceSteps(run, useSourceAvailability());
	if (!steps.length) return null;
	const title = T('prospectingTools.lookup.companySource.title');
	return (
		<section aria-label={title} className="border-b border-grey-4 px-5 pb-2 pt-3">
			<h3 className="text-body-sm font-semibold text-grey-11">{title}</h3>
			<CompanySourceList steps={steps} />
		</section>
	);
};

/**
 * A company result's own card (WEB-13533): where it was found, and nothing of the contact
 * waterfall - its channel tabs and saved order are about finding a person's email and phone.
 * The credits footer stays: an Apollo company lookup on Upsales' key uses them too.
 */
export const CompanySourcesPanel = ({ run, className }: { run: LookupRun | null; className?: string }) => {
	const steps = companySourceSteps(run, useSourceAvailability());
	if (!steps.length) return null;
	const title = T('prospectingTools.lookup.companySource.title');
	return (
		<aside
			aria-label={title}
			className={cn('min-w-0 overflow-hidden rounded-[8px] border border-grey-4 bg-background', className)}
		>
			<h2 className="m-0 px-5 pt-4 text-body-md font-semibold text-brand">{title}</h2>
			<div className="px-5 pb-2">
				<CompanySourceList steps={steps} />
			</div>
			<p className="px-5 pb-5 pt-1 text-body-sm text-grey-11">{T('prospectingTools.lookup.waterfall.footer')}</p>
		</aside>
	);
};

type Loaded = { order: ContactWaterfall; keys: Record<Provider, KeySource> | null };
type Loadable = { state: 'loading' } | { state: 'error' } | { state: 'ready'; value: Loaded };

const useWaterfall = () => {
	const [loaded, setLoaded] = useState<Loadable>({ state: 'loading' });
	useEffect(() => {
		let cancelled = false;
		Promise.all([
			fetchWaterfall(),
			// Without the keys the order still reads; only whose key each step runs on is missing.
			fetchWaterfallKeys().catch((err: unknown) => {
				console.error('[Lookup] waterfall keys failed', err);
				return null;
			})
		])
			.then(([order, keys]) => {
				if (!cancelled) setLoaded({ state: 'ready', value: { order, keys } });
			})
			.catch((err: unknown) => {
				console.error('[Lookup] waterfall order failed', err);
				if (!cancelled) setLoaded({ state: 'error' });
			});
		return () => {
			cancelled = true;
		};
	}, []);
	return loaded;
};

const Steps = ({
	loaded,
	channel,
	run,
	withAirscale
}: {
	loaded: Loaded;
	channel: Channel;
	run: LookupRun | null;
	withAirscale: boolean;
}) => {
	const steps = waterfallSteps(loaded.order[channel], loaded.keys, channel, run, withAirscale);
	if (!steps.length) {
		return <p className="px-5 py-4 text-body-md text-grey-11">{T('prospectingTools.enrichment.step.noneUsed')}</p>;
	}
	return (
		<ol className="m-0 list-none px-5 py-2">
			{steps.map((step, index) => (
				<li
					key={step.provider}
					aria-label={LOOKUP_PROVIDER_NAMES[step.provider]}
					className="flex items-center gap-3 border-b border-grey-2 py-3 last:border-b-0"
				>
					<span
						aria-hidden
						className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-grey-2 text-body-sm font-semibold text-grey-11"
					>
						{index + 1}
					</span>
					<div className="min-w-0 flex-1">
						<div className="text-body-md font-medium text-grey-13">
							{LOOKUP_PROVIDER_NAMES[step.provider]}
						</div>
						{step.keySource ? (
							<div className="text-body-sm text-grey-11">{T(KEY_LABELS[step.keySource])}</div>
						) : null}
					</div>
					<div className="flex flex-wrap justify-end gap-1">
						{step.status ? (
							<span className={cn(PILL, STATUS[step.status].className)}>
								{T(STATUS[step.status].langKey)}
							</span>
						) : null}
						{step.verified ? (
							<span className={cn(PILL, 'bg-success-light text-success-dark')}>
								{T('prospectingTools.lookup.verified')}
							</span>
						) : null}
					</div>
				</li>
			))}
		</ol>
	);
};

/** The saved waterfall order per channel, and what each step did for the shown person. */
const WaterfallPanel = ({ run, className }: { run: LookupRun | null; className?: string }) => {
	const loaded = useWaterfall();
	const available = useSourceAvailability();
	const [channel, setChannel] = useState<Channel>('email');
	const title = T('prospectingTools.lookup.waterfall.title');
	// The order is changed in an admin page (WEB-14634), so only an admin is pointed there.
	const isAdmin = !!useSelf()?.administrator;

	return (
		<aside
			aria-label={title}
			className={cn('min-w-0 overflow-hidden rounded-[8px] border border-grey-4 bg-background', className)}
		>
			<div className="flex items-center gap-2 px-5 pt-4">
				<h2 className="flex-1 text-body-md font-semibold text-brand">{title}</h2>
				{isAdmin ? (
					<a href={`#${ENRICHMENT_PATH}`} className={cn('text-body-sm', LINK)}>
						{T('prospectingTools.lookup.waterfall.changeOrder')}
					</a>
				) : null}
			</div>
			<CompanySources run={run} />
			<div role="tablist" className="flex gap-1 border-b border-grey-4 px-5 pt-3">
				{CHANNEL_TABS.map(tab => {
					const selected = tab.id === channel;
					return (
						<button
							key={tab.id}
							type="button"
							role="tab"
							aria-selected={selected}
							onClick={() => setChannel(tab.id)}
							className={cn(
								'-mb-px border-b-2 px-3 py-2 text-body-md',
								selected ? 'border-brand font-semibold text-brand' : 'border-transparent text-grey-11'
							)}
						>
							{T(tab.langKey)}
						</button>
					);
				})}
			</div>
			{loaded.state === 'ready' ? (
				<Steps loaded={loaded.value} channel={channel} run={run} withAirscale={available.airscale} />
			) : loaded.state === 'loading' ? (
				<p className="px-5 py-4 text-body-md text-grey-11">{T('default.loading')}</p>
			) : (
				<p role="alert" className="px-5 py-4 text-body-md text-danger">
					{T('prospectingTools.enrichment.loadError')}
				</p>
			)}
			<p className="px-5 pb-5 pt-1 text-body-sm text-grey-11">{T('prospectingTools.lookup.waterfall.footer')}</p>
		</aside>
	);
};

export default WaterfallPanel;
